import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../base/qk";
import { 
  assignManager, 
  getAllLocations, 
  getManagersByLocation, 
  removeManagerAssignment 
} from "../../../api/services/settings";
import { 
  CircularProgress, Box, Typography, Tabs, Tab, Button, Paper, 
  List, ListItem, ListItemText, ListItemIcon, Collapse, Stack, 
  Divider, Avatar, alpha, useTheme, Chip
} from "@mui/material";
import { 
  Home as HomeIcon, Add as AddIcon, LocationOn as LocationOnIcon, 
  ExpandLess, ExpandMore, LocationSearching as LocationArrowIcon,
  Person as PersonIcon, Save as SaveIcon, Close as CloseIcon
} from "@mui/icons-material";
import CreateLocationModal from "./CreateLocationModal";
import { getSubLocations } from "../../../api/services/scheduling";
import MultiSelect from "../../FormFields/CustomSelect/MultiSelect";
import CreateAreaModal from "./CreateAreaModal";
import { useSnackbar } from "notistack";
import { searchCandidates } from "../../../api/services/candidate";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";

const Locations = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAreaCreateModal, setShowAreaCreateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [tempSelectedManagerIds, setTempSelectedManagerIds] = useState([]);

  const { data: locations, isLoading, refetch } = useQuery(
    [QK.locations], 
    getAllLocations
  );

  const { data: subLocations, isFetching: isFetchingSubLocations, refetch: subLocationRefetch } = useQuery(
    [QK.subLocations, selectedLocationId],
    () => getSubLocations(selectedLocationId),
    { enabled: !!selectedLocationId }
  );

  const { data: assignedManagers, isLoading: isManagersLoading } = useQuery(
    ['location-managers', selectedLocationId],
    () => getManagersByLocation(selectedLocationId),
    { 
      enabled: !!selectedLocationId,
      staleTime: 0
    }
  );

  useEffect(() => {
    if (assignedManagers) {
      setTempSelectedManagerIds(assignedManagers.map(m => m.candidate) || []);
    } else if (!selectedLocationId) {
      setTempSelectedManagerIds([]);
    }
  }, [assignedManagers, selectedLocationId]);

  const { data: candidates, isLoading: isCandidatesLoading } = useQuery(
    ['search-candidates', "", selectedLocationId],
    () => searchCandidates("", selectedLocationId),
    { 
      enabled: !!selectedLocationId,
      staleTime: 5 * 60 * 1000 
    }
  );

  const userOptions = candidates?.map(c => ({
    value: c.id,
    label: `${c.first_name} ${c.last_name}`
  })) || [];

  const assignMutation = useMutation(assignManager);
  const removeMutation = useMutation(removeManagerAssignment);

  const handleManagerChange = (selectedOptions) => {
    const ids = selectedOptions.map(opt => (typeof opt === 'object' ? opt.value : opt));
    setTempSelectedManagerIds(ids);
  };

  const handleSaveChanges = async () => {
    const currentAssigned = assignedManagers || [];
    const currentIds = currentAssigned.map(m => m.candidate);
    
    const addedIds = tempSelectedManagerIds.filter(id => !currentIds.includes(id));
    const payload = addedIds.map(candidateId => ({
      location: selectedLocationId,
      candidate: candidateId
    }));

    const removedEntries = currentAssigned.filter(m => !tempSelectedManagerIds.includes(m.candidate));

    try {
      const promises = [];
      if (payload.length > 0) promises.push(assignMutation.mutateAsync(payload));
      removedEntries.forEach(m => promises.push(removeMutation.mutateAsync(m.id)));

      if (promises.length > 0) {
        await Promise.all(promises);
        queryClient.invalidateQueries(['location-managers', selectedLocationId]);
        enqueueSnackbar("Managers updated successfully!", { variant: "success" });
      }
      setShowConfirmModal(false);
    } catch (error) {
      enqueueSnackbar("Error updating managers", { variant: "error" });
      setShowConfirmModal(false);
    }
  };

  const handleLocationClick = (id, name) => {
    if (selectedLocationId === id) {
      setSelectedLocationId(null);
      setSelectedLocationName("");
      setTempSelectedManagerIds([]);
    } else {
      setSelectedLocationId(id);
      setSelectedLocationName(name);
      setTempSelectedManagerIds([]);
    }
  };

  const handleCancel = () => {
    setTempSelectedManagerIds(assignedManagers?.map(m => m.candidate) || []);
    enqueueSnackbar("Changes discarded", { variant: "info" });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isUpdating = assignMutation.isLoading || removeMutation.isLoading;

  const selectedValues = userOptions.filter(option => 
    tempSelectedManagerIds.includes(option.value)
  );

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          }}
        >
          <LocationOnIcon sx={{ fontSize: 22 }} />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A" }}>
            Location Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage locations, areas and manager assignments
          </Typography>
        </Box>
        <Chip
          label={`${locations?.length || 0} locations`}
          size="small"
          sx={{
            ml: "auto",
            height: 24,
            fontSize: "0.7rem",
            fontWeight: 600,
            bgcolor: "#F1F5F9",
            color: "#475467",
          }}
        />
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Location Management" />
          <Tab label="Service User Location Management" />
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HomeIcon sx={{ color: "#64748B" }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: "#0F172A" }}>
                All Locations
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                bgcolor: "#2469F0",
                px: 3,
                py: 0.75,
                fontSize: "0.75rem",
                "&:hover": {
                  bgcolor: "#1A5BD4",
                },
              }}
            >
              Add Location
            </Button>
          </Box>

          <List sx={{ p: 0 }}>
            {locations?.map((loc) => (
              <Box key={loc.id} sx={{ mb: 1 }}>
                <ListItem 
                  button 
                  onClick={() => handleLocationClick(loc.id, loc.name)}
                  sx={{ 
                    bgcolor: selectedLocationId === loc.id ? alpha("#2469F0", 0.04) : "#F8FAFC",
                    borderRadius: 1,
                    mb: 0.5,
                    border: selectedLocationId === loc.id ? `1px solid #2469F0` : "1px solid #E2E8F0",
                    "&:hover": { 
                      bgcolor: selectedLocationId === loc.id ? alpha("#2469F0", 0.06) : "#F1F5F9" 
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 35 }}>
                    <LocationOnIcon color={selectedLocationId === loc.id ? "primary" : "error"} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={loc.name} 
                    primaryTypographyProps={{ 
                      fontWeight: selectedLocationId === loc.id ? 700 : 500,
                      color: selectedLocationId === loc.id ? "#2469F0" : "#0F172A"
                    }}
                  />
                  {selectedLocationId === loc.id ? <ExpandLess /> : <ExpandMore />}
                </ListItem>

                <Collapse in={selectedLocationId === loc.id} timeout="auto" unmountOnExit>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      ml: 4.5, 
                      mt: 1, 
                      p: 2, 
                      borderRadius: 2,
                      borderColor: "#E2E8F0",
                      bgcolor: "#FAFBFC"
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="#475467" sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Areas
                      </Typography>
                      <Button 
                        size="small"
                        onClick={() => setShowAreaCreateModal(true)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          color: "#2469F0",
                        }}
                      >
                        + Add Area
                      </Button>
                    </Box>
                    
                    {isFetchingSubLocations ? (
                      <CircularProgress size={20} />
                    ) : subLocations?.length > 0 ? (
                      subLocations.map((s) => (
                        <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                          <LocationArrowIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="#1A1A2E">{s.name}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="#94A3B8" textAlign="center" py={1}>
                        No areas found.
                      </Typography>
                    )}
                  </Paper>
                </Collapse>
              </Box>
            ))}
          </List>
        </Box>

        <Box sx={{ width: "600px", flexShrink: 0 }}>
          {selectedLocationId && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                position: 'relative' 
              }}
            >
              {(isManagersLoading || isCandidatesLoading || isUpdating) && (
                <CircularProgress 
                  size={20} 
                  sx={{ position: 'absolute', top: 16, right: 16 }} 
                />
              )}
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <LocationOnIcon color="error" />
                <Typography variant="h6" fontWeight={600} sx={{ color: "#0F172A" }}>
                  {selectedLocationName}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" fontWeight={600} color="#475467" sx={{ textTransform: "uppercase", letterSpacing: "0.3px", display: "block", mb: 1 }}>
                  Assign Managers
                </Typography>
                <MultiSelect
                  options={userOptions}
                  value={selectedValues} 
                  onChange={handleManagerChange}
                  placeholder={isCandidatesLoading ? "Loading..." : "Select Managers"}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={handleCancel} 
                  disabled={isUpdating}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "8px",
                    borderColor: "#D0D5DD",
                    color: "#667085",
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                      borderColor: "#98A2B3",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isUpdating}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    bgcolor: "#2469F0",
                    px: 3,
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                    },
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      <CreateLocationModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        refetch={refetch}
      />

      <CreateAreaModal
        locationId={selectedLocationId}
        show={showAreaCreateModal}
        handleClose={() => setShowAreaCreateModal(false)}
        refetch={subLocationRefetch}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleSaveChanges}
        title="Update Managers"
        body={`Are you sure you want to update the assigned managers for ${selectedLocationName}?`}
        isLoading={isUpdating}
        confirmText="Yes, Update"
        cancelText="No, Cancel"
      />
    </Box>
  );
};

export default Locations;